import React, { useEffect, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputAdornment,
  IconButton,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import { useAuthUser } from "react-auth-kit"
import { AlertColor } from "@mui/material/Alert"
import FormHelperText from "@mui/material/FormHelperText"
import { AutoAwesome, Visibility, VisibilityOff } from "@mui/icons-material"
import api from "../utils/api"

interface Admin {
  name: string
  username: string
  phone: string
  password: string
  eventId: string
  id: string
  type: string
}

interface SnackbarState {
  open: boolean
  message: string
  severity?: AlertColor
}

interface GroupedAdmins {
  [type: string]: Admin[]
}

const groupAdminsByType = (admins: Admin[]): GroupedAdmins => {
  return admins.reduce((acc: GroupedAdmins, admin) => {
    if (!acc[admin.type]) acc[admin.type] = []
    acc[admin.type].push(admin)
    return acc
  }, {})
}

const formatAdminType = (type: string): string => {
  const types: { [key: string]: string } = {
    appAdmin: "App",
    eventAdmin: "Event",
  }

  return types[type] || type.charAt(0).toUpperCase() + type.slice(1)
}

function generatePassword() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
  const numbers = "0123456789"
  const specialChars = "!@#$%^&*"

  const pickRandom = (set: string) =>
    set.charAt(Math.floor(Math.random() * set.length))

  return (
    pickRandom(chars) +
    pickRandom(numbers) +
    pickRandom(specialChars) +
    Array.from({ length: Math.floor(Math.random() * 8) + 3 }, () =>
      pickRandom(chars + numbers + specialChars)
    ).join("")
  )
}

export const ManageAdminsForm = () => {
  const [admins, setAdmins] = useState<Admin[]>([])
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editedAdmin, setEditedAdmin] = useState<Admin | null>(null)
  const [name, setName] = useState("")
  const [username, setUsername] = useState("")
  const [phone, setPhone] = useState("")
  const [password, setPassword] = useState("")
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [snackbar, setSnackbar] = React.useState<SnackbarState>({
    open: false,
    message: "",
    severity: undefined,
  })
  const [showPassword, setShowPassword] = useState(false)
  const auth = useAuthUser()
  const currentAdminUsername = auth()?.username

  useEffect(() => {
    ;(async () => {
      try {
        setIsLoading(true)
        const response = await api.get("/admin/active")
        const data: Admin[] = response.data
        setAdmins(data)
        setIsLoading(false)
      } catch (error) {
        console.error("Error fetching active admins:", error)
        setIsLoading(false) // You might want to also handle errors more gracefully, perhaps with another piece of state.
      }
    })()
  }, [])

  const handleOpenEditModal = (admin: Admin) => {
    setEditedAdmin(admin)
    setName(admin.name)
    setUsername(admin.username)
    setPhone(admin.phone)
    setIsEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setPassword("")
    setEditedAdmin(null)
    setIsEditModalOpen(false)
  }

  const isUsernameUnique = (usernameToCheck: string, idToExclude: string) => {
    return !admins.some(
      (admin) => admin.username === usernameToCheck && admin.id !== idToExclude
    )
  }

  // valid password test
  const isValidPassword = (password: string) => {
    const regex = /^(?=.*[0-9])(?=.*[!@#$%^&*]).{6,}$/
    return regex.test(password)
  }

  const handleUpdateAdmin = async () => {
    if (phone && !isValidPhone(phone)) return
    if (!editedAdmin) return
    if (!name || !username || !phone) {
      return
    }

    // Check if none of the fields have changed
    if (
      username === editedAdmin.username &&
      name === editedAdmin.name &&
      phone === editedAdmin.phone &&
      password === ""
    ) {
      handleCloseEditModal()
      setSnackbar({
        open: true,
        message: "Everything is up to date!",
        severity: "info",
      })
      return
    }

    if (!isUsernameUnique(username, editedAdmin.id)) {
      setSnackbar({
        open: true,
        message:
          "This username is already taken. Please choose a different one.",
        severity: "error",
      })
      return
    }

    const updatedAdmin = {
      ...editedAdmin,
      name,
      username,
      phone,
      //eventId,
    }

    // updates the password  feild then sets it to blank so the hashed value isnt printed on the next iteration

    if (password !== "") {
      //checks new password against valid passwords
      if (!isValidPassword(password)) {
        setSnackbar({
          open: true,
          message: "Password does not meet requirements",
          severity: "error",
        })
        console.error("Invalid password.")
        return
      } else {
        updatedAdmin.password = password
        setPassword("")
      }
    }

    try {
      await api.put(`/admin/${editedAdmin.id}`, updatedAdmin)

      handleCloseEditModal()

      // Update the state to reflect the edited admin:
      setAdmins((prevAdmins) => {
        return prevAdmins.map((admin) => {
          if (admin.id === editedAdmin.id) {
            return updatedAdmin
          }

          return admin
        })
      })

      setSnackbar({
        open: true,
        message: "Admin updated successfully.",
        severity: "success",
      })
    } catch (error) {
      console.error("Error updating admin:", error)
    }
  }

  const handleDeleteAdmin = (admin: Admin) => {
    setAdminToDelete(admin)
    setIsDeleteConfirmationOpen(true)
  }

  const confirmDeleteAdmin = async () => {
    if (!adminToDelete) return

    try {
      await api.delete(`/admin/${adminToDelete.id}`)

      setAdmins((prevAdmins) =>
        prevAdmins.filter((admin) => admin.id !== adminToDelete.id)
      )
      setIsDeleteConfirmationOpen(false)
      setSnackbar({
        open: true,
        message: "Admin deleted successfully.",
        severity: "success",
      })
    } catch (error) {
      console.error("Error deleting admin:", error)
      setSnackbar({
        open: true,
        message: "Failed to delete admin. Please try again.",
        severity: "error",
      })
    }
  }

  const isValidPhone = (phone: string) => /^(\d{10})$/.test(phone)
  const groupedAdmins = groupAdminsByType(admins)

  return (
    <>
      <Typography variant="h6" m={4}>
        Manage Admins
      </Typography>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Operations</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedAdmins).map(([type, adminsList]) => (
                <React.Fragment key={type}>
                  <TableRow
                    className={
                      type === "appAdmin" || type === "eventAdmin"
                        ? "lightGreyRow"
                        : ""
                    }
                  >
                    <TableCell colSpan={5} style={{ padding: "10px 16px" }}>
                      <Typography variant="subtitle1">
                        {formatAdminType(type)} Admins
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {adminsList.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell>{admin.name}</TableCell>
                      <TableCell>{admin.username}</TableCell>
                      <TableCell>{admin.phone}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          style={{ backgroundColor: "#0047AB" }}
                          aria-label="Edit"
                          onClick={() => handleOpenEditModal(admin)}
                          sx={{ marginRight: 1 }}
                          disabled={admin.username === currentAdminUsername} // Disable if the admin is the logged-in user
                        >
                          <EditIcon />
                        </Button>
                        <Button
                          variant="contained"
                          style={{ backgroundColor: "red" }}
                          aria-label="Delete"
                          onClick={() => handleDeleteAdmin(admin)}
                          disabled={admin.username === currentAdminUsername} // Disable if the admin is the logged-in user
                        >
                          <DeleteIcon />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={isEditModalOpen} onClose={handleCloseEditModal}>
        <DialogTitle>Edit Admin</DialogTitle>
        <DialogContent>
          {editedAdmin && (
            <form>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                sx={{ marginY: 2 }}
              />
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                fullWidth
                required
                sx={{ marginY: 2 }}
              />
              <TextField
                label="Phone"
                variant="outlined"
                fullWidth
                margin="normal"
                value={phone}
                required
                onChange={(e) => setPhone(e.target.value)}
                error={!!(phone && !/^(\d{10})$/.test(phone))}
                helperText={
                  phone && !/^(\d{10})$/.test(phone)
                    ? "Please enter a 10-digit number."
                    : ""
                }
                inputProps={{
                  pattern: "\\d{10}",
                }}
              />
              <FormControl fullWidth sx={{ marginY: 2 }}>
                <TextField
                  label="Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value.replace(/\s/g, ""))
                  }
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={() => setPassword(generatePassword())}
                          edge="start"
                        >
                          <AutoAwesome />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormHelperText sx={{ color: "red" }}>
                  If you prefer to keep the current password, leave this field
                  blank.
                </FormHelperText>
              </FormControl>
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpdateAdmin} color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this admin?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteConfirmationOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={confirmDeleteAdmin} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
